import React from "react"
import { FormattedMessage } from "react-intl"
import { Avatar, Badge, Box, Button, Stack, Tile, Typography } from "@carrotfertility/carotene-core"
import {
  useCarrotCompanion,
  useHasUnreadMessagesFromCarrotCompanion
} from "#/components/care-team/hooks/useCarrotCompanion"
import { useHistory } from "react-router-dom"
import { useSendMessagePath } from "#/components/talk-to-carrot/hooks/useSendMessagePath"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { formatCarrotCompanionName } from "./utils/carrotCompanionNameUtil"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"

export function CarrotCompanionTile(): JSX.Element {
  const {
    data: carrotCompanion,
    isLoading: isCarrotCompanionLoading,
    isError: isCarrotCompanionError
  } = useCarrotCompanion()
  const { data: unreadMessages } = useHasUnreadMessagesFromCarrotCompanion()
  const { isPartnerAccessingAccount } = useUserRole()

  const history = useHistory()
  const sendMessagePath = useSendMessagePath()
  const { mutate: trackAction } = useMemberActionTracking()

  const handleClick = () => {
    trackAction({
      memberActionTaken: MemberActions.HOMEPAGE_CARROT_COMPANION_MESSAGE_CLICKED,
      metaData: {
        hasUnreadMessages: unreadMessages?.hasUnreadMessages || false
      }
    })
    history.push(sendMessagePath)
  }

  const { displayName, initials } = formatCarrotCompanionName(carrotCompanion?.name)

  if (isCarrotCompanionError) {
    return null
  }

  return (
    !isCarrotCompanionLoading && (
      <Tile
        component={Box}
        color="info"
        sx={(theme) => ({
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.primary.accent}`,
          containerType: "inline-size"
        })}
      >
        <Stack
          gap={(theme) => theme.tokens.spacing.md}
          justifyContent="space-between"
          sx={{
            flexDirection: "row",
            "@container (max-width: 420px)": {
              flexDirection: "column"
            }
          }}
        >
          <Stack gap={(theme) => theme.tokens.spacing.xs} direction="row">
            <Badge
              color="primary"
              overlap="circular"
              badgeContent=" "
              variant="dot"
              invisible={!unreadMessages?.hasUnreadMessages}
            >
              {carrotCompanion?.photoUrl ? (
                <Avatar src={carrotCompanion.photoUrl} alt={displayName} />
              ) : (
                <Avatar sx={{ bgcolor: (theme) => theme.palette.warning.contrastText }}>{initials}</Avatar>
              )}
            </Badge>

            <Stack>
              <Typography variant="h5">{displayName}</Typography>
              <Typography variant="body2">
                <FormattedMessage defaultMessage="Your Carrot Companion" />
              </Typography>
            </Stack>
          </Stack>

          <Stack alignItems="start">
            {!isPartnerAccessingAccount && unreadMessages?.hasUnreadMessages ? (
              <Button
                onClick={handleClick}
                color="primary"
                variant="contained"
                fullWidth={false}
                data-analytics-id="care-team-carrot-companion-default-view-new-message-button"
              >
                <FormattedMessage defaultMessage="View new message" />
              </Button>
            ) : (
              <Button
                onClick={handleClick}
                color="secondary"
                variant="outlined"
                fullWidth={false}
                data-analytics-id="care-team-carrot-companion-default-send-message-button"
              >
                <FormattedMessage defaultMessage="Send a message" />
              </Button>
            )}
          </Stack>
        </Stack>
      </Tile>
    )
  )
}
